/**
 * MIKSTURY — definicje konsumowalnych mikstur kupowanych za 💎 u capibarki-handlarki.
 *
 * Mikstura = sink anty-inflacyjny: 💎 wydane na nią ZNIKA z gospodarki (idzie do
 * NPC, nie do innego gracza). Kupujesz → trzymasz w plecaku → używasz, co daje
 * CZASOWY buff (+% złota / +% szczęścia / +% siły). Buff wygasa po `durationSec`.
 *
 * ⭐ ŻEBY ZMIENIĆ CENĘ / SIŁĘ / CZAS mikstury — edytuj WYŁĄCZNIE ten plik...
 *    ...ALE pamiętaj o mirrorze serwerowym `server/api/src/game-config/potions.ts`
 *    (serwer waliduje cenę i ustawia buff — musi znać te same liczby). To formuły/
 *    dane, nie generator — synchronizuj ręcznie (jak `balance.config` ↔ `balance.ts`).
 *
 * Anty-inflacja (patrz plan): zwykła mikstura ~= 1 krótka sesja farmienia, a
 * `potion-combo` jest DROGA — pochłania duże zapasy 💎 bogatych graczy.
 */

import type { ID } from '@/domain/types/common';

/** Statystyki, na które działają mikstury. Każdy = jeden punkt wpięcia mnożnika. */
export type EffectStat = 'gold' | 'luck' | 'strength';

/** Pojedynczy efekt mikstury: który stat i jaki mnożnik (1.5 = +50%). */
export interface PotionEffect {
  stat: EffectStat;
  /** Mnożnik. 1.5 = +50%. Combo może mieć po jednym wpisie na każdy stat. */
  mult: number;
}

/** Definicja mikstury (dane statyczne — cena, czas, efekty). */
export interface PotionDef {
  id: ID;
  /** Nazwa pokazywana w UI (PL). */
  label: string;
  /** Krótki opis działania (PL) — pokazywany na karcie w sklepie. */
  description: string;
  /** Ikona PNG/WebP w assets/ui/ (renderowana przez <Icon>, NIGDY inline emoji). */
  icon: string;
  /** Cena w 💎 za 1 sztukę. */
  priceGems: number;
  /** Czas działania buffa po użyciu, w sekundach. */
  durationSec: number;
  /** Lista efektów. Pojedyncza mikstura = 1 efekt; combo = 3. */
  effects: PotionEffect[];
}

/**
 * Aktywne buffy gracza — mapa stat → { mnożnik, kiedy wygasa (epoch ms) }.
 * Wygaszanie LENIWE: przy odczycie sprawdzamy `expiresAt > now`, brak sprzątacza.
 * Trzymane w `PlayerSave.activeEffects` (server-authoritative — ustawiane przez
 * `POST /potions/use`).
 */
export type ActiveEffects = Partial<Record<EffectStat, { mult: number; expiresAt: number }>>;

/** Plecak mikstur — mapa defId → liczba sztuk. Trzymane w `PlayerSave.potions`. */
export type PotionsBag = Record<string, number>;

/**
 * Katalog 4 mikstur (v1).
 *
 * Strojenie balansu — wszystkie liczby tutaj:
 *  - mult: 1.5 = +50% (combo +60%).
 *  - durationSec: 1800 = 30 min (combo 3600 = 60 min).
 *  - priceGems: zwykłe 60–90, combo 300 (premium sink).
 */
export const POTIONS: readonly PotionDef[] = [
  {
    id: 'potion-gold',
    label: 'Mikstura Złota',
    description: '+50% złota z niszczenia obiektów przez 30 min.',
    icon: '/ui/ui_potion_gold.webp',
    priceGems: 60,
    durationSec: 1800,
    effects: [{ stat: 'gold', mult: 1.5 }],
  },
  {
    id: 'potion-luck',
    label: 'Mikstura Szczęścia',
    description: '+50% szansy na rzadkie pety (huge/titanic) przez 30 min.',
    icon: '/ui/ui_potion_luck.webp',
    priceGems: 90,
    durationSec: 1800,
    effects: [{ stat: 'luck', mult: 1.5 }],
  },
  {
    id: 'potion-strength',
    label: 'Mikstura Siły',
    description: '+50% obrażeń petów (szybsze niszczenie) przez 30 min.',
    icon: '/ui/ui_potion_strength.webp',
    priceGems: 60,
    durationSec: 1800,
    effects: [{ stat: 'strength', mult: 1.5 }],
  },
  {
    id: 'potion-combo',
    label: 'Wielka Mikstura',
    description: '+60% złota, szczęścia i siły naraz przez 60 min. Premium!',
    icon: '/ui/ui_potion_combo.webp',
    priceGems: 300,
    durationSec: 3600,
    effects: [
      { stat: 'gold', mult: 1.6 },
      { stat: 'luck', mult: 1.6 },
      { stat: 'strength', mult: 1.6 },
    ],
  },
] as const;

/** Szybkie wyszukiwanie definicji po id. */
export const POTIONS_BY_ID: Record<string, PotionDef> = Object.fromEntries(
  POTIONS.map((p) => [p.id, p]),
);

/**
 * Aktualny mnożnik danego statu z aktywnych buffów. Zwraca 1 gdy brak buffa
 * albo wygasł. Współdzielone przez wszystkie punkty wpięcia (złoto/siła klient).
 *
 * @param effects mapa z `PlayerSave.activeEffects`
 * @param stat    który stat
 * @param now     `Date.now()` (parametr dla testowalności)
 */
export function activeEffectMult(
  effects: ActiveEffects | undefined,
  stat: EffectStat,
  now: number,
): number {
  const e = effects?.[stat];
  if (e && e.expiresAt > now) return e.mult;
  return 1;
}
